import React from 'react';

import { Link, Text } from 'ds';

import type { ConsumablesPickerConfirmedApplyMode } from './AIAssistantConsumablesPickerConfirmedTypes';

const MESSAGE_TEXT_STYLE: React.CSSProperties = {
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.32px',
  width: '100%'
};

function escapeHtmlAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function isAppPathHref(href: string): boolean {
  return href.length > 0 && href[0] === '/';
}

/** HTML for the typewriter effect; mirrors {@link AIAssistantConsumablesMessageCopy} selection copy. */
export function buildConsumablesSelectionIntroHtml(orderEditUrl: string | null): string {
  const linkStyle = 'font-size:16px;line-height:24px;letter-spacing:0.32px';
  const linkedNextOrder =
    orderEditUrl != null
      ? `your <a href="${escapeHtmlAttr(orderEditUrl)}" target="_blank" rel="noopener noreferrer" style="${linkStyle}">next order</a>`
      : 'your next order';
  return `<p>Here are some matching snacks and pantry items. Pick what you want in the list below — you can adjust quantities in ${linkedNextOrder} when you're done.</p>`;
}

export type AIAssistantConsumablesMessageCopyProps =
  | { phase: 'selection'; orderEditUrl: string | null }
  | {
      phase: 'confirmed';
      applyMode: ConsumablesPickerConfirmedApplyMode;
      subscriptionPath: string;
      nextOrderLockPhrase: string | null;
      nextOrderEditPath: string | null;
      fallbackOrderEditUrl: string | null;
    };

function SelectionNextOrderLink({ orderEditUrl }: { orderEditUrl: string | null }) {
  return (
    <>
      your{' '}
      {orderEditUrl != null ? (
        <Link href={orderEditUrl} target="_blank" size="body-md" semibold={false}>
          next order
        </Link>
      ) : (
        <Text inline size="body-md" color="gray-900">
          next order
        </Text>
      )}
    </>
  );
}

const AIAssistantConsumablesMessageCopy: React.FC<AIAssistantConsumablesMessageCopyProps> = props => {
  const { phase } = props;

  if (phase === 'selection') {
    const { orderEditUrl } = props;
    return (
      <div style={MESSAGE_TEXT_STYLE}>
        <Text size="body-md" color="gray-900">
          Here are some matching snacks and pantry items. Pick what you want in the list below — you can adjust
          quantities in <SelectionNextOrderLink orderEditUrl={orderEditUrl} /> when you&apos;re done.
        </Text>
      </div>
    );
  }

  const { applyMode, subscriptionPath, nextOrderLockPhrase, nextOrderEditPath, fallbackOrderEditUrl } = props;

  if (applyMode === 'recurring') {
    return (
      <div style={MESSAGE_TEXT_STYLE}>
        <Text size="body-md" color="gray-900">
          Done — I&apos;ve added those to your subscription. You can make changes anytime from{' '}
          <Link href={subscriptionPath} size="body-md" semibold={false}>
            your pantry page
          </Link>
          .
        </Text>
      </div>
    );
  }

  const nextOrderHref = nextOrderEditPath ?? fallbackOrderEditUrl;
  const nextOrderTarget = nextOrderHref != null && isAppPathHref(nextOrderHref) ? undefined : '_blank';

  return (
    <div style={MESSAGE_TEXT_STYLE}>
      <Text size="body-md" color="gray-900">
        Done — I&apos;ve added those to your next order. You can make changes
        {nextOrderLockPhrase != null ? <> before {nextOrderLockPhrase}</> : null} by editing{' '}
        {nextOrderHref != null ? (
          <Link href={nextOrderHref} target={nextOrderTarget} size="body-md" semibold={false}>
            your next order
          </Link>
        ) : (
          'your next order'
        )}
        .
      </Text>
    </div>
  );
};

export default AIAssistantConsumablesMessageCopy;
